import { persistentAtom } from '@nanostores/persistent';
import { action, computed, atom } from 'nanostores';
import Decimal from 'decimal.js';
import {
  patientWalletBalance,
  activePatientDiscounts,
  type PatientDiscount,
  patient,
} from './patient';
import client from '../graphql/client';

import { PurchaseFromStorefrontDocument } from '../graphql/mutations/purchaseFromStorefront.generated';

export type CartItem = {
  cartItem: any;
  type: string;
  quantity?: number;
};

// Define a type for the persisted data including an expiration timestamp
type PersistentCart = {
  items: CartItem[];
  expires: number; // Timestamp in milliseconds
};

// Calculate expiration time (3 days in milliseconds)
const THREE_DAYS_MS = 3 * 24 * 60 * 60 * 1000;

export const cart = persistentAtom<CartItem[]>('cart', [], {
  encode: (value: CartItem[]) => {
    const dataToStore: PersistentCart = {
      items: value,
      expires: Date.now() + THREE_DAYS_MS,
    };
    return JSON.stringify(dataToStore);
  },
  decode: (value: string) => {
    try {
      const storedData: PersistentCart = JSON.parse(value);
      // Check if the stored data has an expiration and if it's still valid
      if (storedData.expires && storedData.expires > Date.now()) {
        return storedData.items || []; // Return items if not expired
      }
    } catch (e) {
      console.error('Error decoding cart data from storage:', e);
    }
    return []; // Return empty array if expired, invalid, or error occurred
  },
});

export const purchaseInProgress = atom<boolean>(false);
export const purchaseError = atom<string | null>(null);
export const invoiceConfirmationId = atom<string | null>(null);
export const useWalletBalance = atom<boolean>(false);
export const testingAmountOverride = atom<number | null>(null);
export const shippingMethod = atom<string>('');

export const setShippingMethod = action(
  shippingMethod,
  'setShippingMethod',
  (store, method) => {
    store.set(method);
  }
);

export const setTestingAmountOverride = (value) => {
  console.log('setting testing amount override', value);
  testingAmountOverride.set(value);
};

export const setUseWalletBalance = action(
  useWalletBalance,
  'useWalletBalance',
  (store, value) => {
    store.set(value);
  }
);

export async function purchase(data) {
  const currentCart = [...computedCart.get()];
  const patientDiscounts = activePatientDiscounts.get(); // Get current discounts

  const { turnstileToken, ...rest } = data;
  const input = {
    ...rest,
    turnstileToken,
    ...(useWalletBalance.get() && {
      walletAmountToUse: ledger.get().walletUsedNumber, // walletUsedNumber is already in cents
    }),
    workspaceId: data.workspaceId,
    memberships: currentCart
      .filter((item: CartItem) => item.type === 'membership')
      .map((item: CartItem & { quantity: number }) => {
        // Ensure item has quantity for helper
        const discountDetails = calculateLineItemDiscountDetails(
          item,
          patientDiscounts
        );
        return {
          membershipId: item.cartItem.id,
          taxRate: item.cartItem.taxRate,
          name: item.cartItem.name,
          pricePerUnit: item.cartItem.price, // pricePerUnit is in cents
          quantity: item.quantity, // Ensure quantity is from computedCart item
          allowGratuity: false,
          discountAmount: discountDetails.discountAmount, // in cents, from helper
          discountType: discountDetails.discountType, // 'fixed', from helper
          type: 'membership',
          useCount: 0,
        };
      }),
    packages: currentCart
      .filter((item: CartItem) => item.type === 'package')
      .map((item: CartItem & { quantity: number }) => {
        const discountDetails = calculateLineItemDiscountDetails(
          item,
          patientDiscounts
        );
        return {
          packageId: item.cartItem.id,
          taxRate: item.cartItem.taxRate,
          useCount: 0,
          quantity: item.quantity,
          name: item.cartItem.name,
          pricePerUnit: item.cartItem.price,
          allowGratuity: false,
          discountAmount: discountDetails.discountAmount,
          discountType: discountDetails.discountType,
          type: 'package',
        };
      }),
    promotions: currentCart
      .filter((item: CartItem) => item.type === 'promotion')
      .map((item: CartItem & { quantity: number }) => {
        const discountDetails = calculateLineItemDiscountDetails(
          item,
          patientDiscounts
        );
        return {
          promotionId: item.cartItem.id,
          quantity: item.quantity,
          name: item.cartItem.name,
          pricePerUnit: item.cartItem.price,
          allowGratuity: false,
          discountAmount: discountDetails.discountAmount,
          discountType: discountDetails.discountType,
          taxRate: item.cartItem.taxRate ?? 0, // Promotions can have tax rates, provide default
          type: 'promotion',
          useCount: 0,
        };
      }),
    services: currentCart // 'product' in cartItem.type corresponds to 'services' in backend
      .filter((item: CartItem) => item.type === 'product')
      .map((item: CartItem & { quantity: number }) => {
        const discountDetails = calculateLineItemDiscountDetails(
          item,
          patientDiscounts
        );
        return {
          serviceId: item.cartItem.id,
          taxRate: item.cartItem.taxRate,
          pricePerUnit: item.cartItem.price,
          quantity: item.quantity,
          name: item.cartItem.name,
          allowGratuity: false,
          discountAmount: discountDetails.discountAmount,
          discountType: discountDetails.discountType,
          type: 'product', // This should match the backend expectation, often 'service' or 'product'
          useCount: 0,
        };
      }),
    lineItems: currentCart
      .filter((item: CartItem) => item.type === 'shipping')
      .map((item: CartItem & { quantity: number }) => {
        // Shipping generally won't have discounts from patientDiscounts
        return {
          name: item.cartItem.name,
          pricePerUnit: item.cartItem.price,
          quantity: item.quantity, // typically 1 for shipping unless designed otherwise
          allowGratuity: false,
          discountAmount: 0, // Explicitly no discount for shipping from this system
          discountType: 'fixed',
          taxRate: item.cartItem.taxRate ?? 0, // Shipping might have tax or not
          type: 'other', // Or 'shipping' if backend supports it
          useCount: 0,
        };
      }),
  };
  purchaseError.set(null);
  purchaseInProgress.set(true);

  try {
    const currentPatient = patient.get(); // Get current patient state
    const variablesInput = {
      ...input,
      ...(currentPatient &&
        currentPatient.patientId && { patientId: currentPatient.patientId }), // Add patientId if available
    };

    const purchaseResult = await client.mutate({
      mutation: PurchaseFromStorefrontDocument,
      variables: {
        input: variablesInput, // Use the modified input with patientId
      },
    });

    invoiceConfirmationId.set(
      purchaseResult.data.purchaseFromStorefront.invoiceId
    );
  } catch (err) {
    purchaseError.set(err.message);
  }

  purchaseInProgress.set(false);

  try {
    (window as any).DD_RUM.setUser({
      email: data.email,
    });
  } catch (err) {}
}

export function addToCart(cartItem, type) {
  let currentCart = [...cart.get()];

  if (
    type === 'shipping' &&
    currentCart.some(
      (item) => item.cartItem.id === cartItem.id && item.type === type
    )
  ) {
    currentCart = currentCart.map((item) =>
      item.type === 'shipping' ? { cartItem, type } : item
    );
    cart.set(currentCart);
    return;
  }
  // Check if the type is 'membership' or 'package' and if it already exists in the cart
  if (
    (type === 'membership' || type === 'package') &&
    currentCart.some(
      (item) => item.cartItem.id === cartItem.id && item.type === type
    )
  ) {
    return;
  }

  currentCart.push({ cartItem, type });
  cart.set(currentCart);
}

export const removeFromCart = action(
  cart,
  'removeFromCart',
  (store, cartItem) => {
    const currentCart = [...store.get()];
    const existingItemIndex = currentCart.findIndex(
      (item) => item.cartItem.id === cartItem.cartItem.id
    );

    if (existingItemIndex > -1) {
      // Remove the item from the cart
      const newCart = [
        ...currentCart.slice(0, existingItemIndex),
        ...currentCart.slice(existingItemIndex + 1),
      ];
      const hasShippableItems = newCart.some(
        (item) => item.cartItem.isShippable
      );
      if (!hasShippableItems) {
        const noShippingCart = newCart.filter(
          (item) => item.type !== 'shipping'
        );
        store.set(noShippingCart);
      } else {
        store.set(newCart);
      }
    }

    return store.get();
  }
);

export function quantityOfItemIncart(cartItem) {
  const items = cart.get().filter((item) => item.cartItem.id === cartItem.id);
  return items.length;
}

const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

// Helper function to calculate discounts for a line item
const calculateLineItemDiscountDetails = (
  cartLineItem: CartItem & { quantity: number },
  patientDiscounts: PatientDiscount[] | null
) => {
  let lineItemTotalDiscountDecimal = new Decimal(0);
  const itemPriceDecimal = new Decimal(cartLineItem.cartItem.price); // Price is in cents
  // itemSubtotalDecimal is price * quantity, still in cents for precision initially
  const itemSubtotalDecimal = itemPriceDecimal.mul(cartLineItem.quantity);

  if (patientDiscounts && patientDiscounts.length > 0) {
    patientDiscounts.forEach((discount) => {
      let appliesToItem = false;
      const discountRuleType = discount.type?.toLowerCase(); // e.g. 'category', 'product', 'service'
      const cartItemType = cartLineItem.type?.toLowerCase(); // e.g. 'product', 'membership'
      const cartItemId = cartLineItem.cartItem.id;
      // Assuming cartLineItem.cartItem might have a categoryId for 'category' discounts.
      // This field needs to be present on the cart item for category discounts to apply.
      const cartItemCategoryId = cartLineItem.cartItem.categoryId;

      switch (discountRuleType) {
        case 'category':
          if (
            discount.categoryId &&
            cartItemCategoryId === discount.categoryId
          ) {
            appliesToItem = true;
          }
          break;
        case 'membership':
          if (
            cartItemType === 'membership' &&
            discount.membershipId === cartItemId
          ) {
            appliesToItem = true;
          }
          break;
        case 'package':
          if (cartItemType === 'package' && discount.packageId === cartItemId) {
            appliesToItem = true;
          }
          break;
        case 'product':
          // The discount type is 'product', use 'serviceId' from the discount to match 'cartItemId'.
          if (cartItemType === 'product' && discount.serviceId === cartItemId) {
            appliesToItem = true;
          }
          break;
        case 'service':
          // The discount type is 'service', use 'serviceId' from the discount to match 'cartItemId'.
          // A service in the cart could have type 'service' or 'product'.
          if (
            (cartItemType === 'service' || cartItemType === 'product') &&
            discount.serviceId === cartItemId
          ) {
            appliesToItem = true;
          }
          break;
        case 'promotion':
          // This discount applies if the cart item's ID matches the promotionId of the discount rule.
          if (discount.promotionId && cartItemId === discount.promotionId) {
            appliesToItem = true;
          }
          break;
        default:
          // Optional: Log unknown or unhandled discount types for debugging.
          // console.warn(`Unknown discount type encountered: ${discount.type}`);
          break;
      }

      if (appliesToItem) {
        let currentDiscountValueDecimal = new Decimal(0);
        const lowerDiscountAmountType = discount.discountType.toLowerCase(); // 'percentage' or 'fixed'

        if (lowerDiscountAmountType === 'percentage') {
          // Ensure discount.discountAmount for percentages is a decimal factor (e.g., 0.05 for 5%)
          currentDiscountValueDecimal = itemSubtotalDecimal.mul(
            new Decimal(discount.discountAmount)
          );
        } else if (lowerDiscountAmountType === 'fixed') {
          // Ensure discount.discountAmount for fixed is in cents and applied per unit
          currentDiscountValueDecimal = new Decimal(
            discount.discountAmount
          ).mul(cartLineItem.quantity);
        }
        lineItemTotalDiscountDecimal = lineItemTotalDiscountDecimal.plus(
          currentDiscountValueDecimal
        );
      }
    });
  }

  // Ensure discount doesn't exceed item subtotal (all values in cents here)
  if (lineItemTotalDiscountDecimal.greaterThan(itemSubtotalDecimal)) {
    lineItemTotalDiscountDecimal = itemSubtotalDecimal;
  }

  return {
    discountAmount: lineItemTotalDiscountDecimal.round().toNumber(), // For backend (in cents)
    discountType: 'fixed', // For the backend, we send a pre-calculated fixed amount
    discountValueDecimal: lineItemTotalDiscountDecimal.div(100), // For ledger calculations (in dollars)
  };
}; // End of calculateLineItemDiscountDetails

export const computedCart = computed([cart], (currentCartItems) => {
  const groupedCart = currentCartItems.reduce(
    (acc: (CartItem & { quantity: number })[], curr) => {
      const existingItem = acc.find(
        (item) =>
          item.cartItem.id === curr.cartItem.id &&
          ['product', 'promotion'].includes(item.type)
      );
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        if (curr.type === 'product' || curr.type === 'promotion') {
          acc.push({ ...curr, quantity: 1 });
        } else if (['package', 'membership', 'shipping'].includes(curr.type)) {
          const existingPackageOrMembership = acc.find(
            (item) =>
              item.cartItem.id === curr.cartItem.id &&
              ['package', 'membership', 'shipping'].includes(item.type)
          );
          if (!existingPackageOrMembership) {
            acc.push({ ...curr, quantity: 1 });
          }
        }
      }
      return acc;
    },
    [] as (CartItem & { quantity: number })[]
  );
  return groupedCart;
});

export const ledger = computed(
  [cart, useWalletBalance, activePatientDiscounts, computedCart],
  (currentCart, uWalletBalance, patientDiscounts, cCart) => {
    let subtotalPreDiscountDecimal = new Decimal(0);
    let totalDiscountAppliedDecimal = new Decimal(0);
    let subtotalAfterDiscountDecimal = new Decimal(0);
    let totalTaxDecimal = new Decimal(0);
    let walletBalanceDecimal = new Decimal(patientWalletBalance.get() ?? 0); // in cents

    // Use computedCart as it has quantities correctly calculated for products/promotions
    const itemsInCart = cCart; // cCart comes from computedCart dependency

    itemsInCart.forEach((lineItem: CartItem & { quantity: number }) => {
      const itemPriceDecimal = new Decimal(lineItem.cartItem.price); // Price is in cents
      const itemOriginalSubtotalDecimal = itemPriceDecimal.mul(
        lineItem.quantity
      ); // Original subtotal for this line item, in cents
      subtotalPreDiscountDecimal = subtotalPreDiscountDecimal.plus(
        itemOriginalSubtotalDecimal
      );

      const discountDetails = calculateLineItemDiscountDetails(
        lineItem,
        patientDiscounts
      );
      // discountDetails.discountValueDecimal is in dollars, convert to cents for consistent calculation
      const lineItemDiscountInCents = new Decimal(
        discountDetails.discountValueDecimal
      ).mul(100);
      totalDiscountAppliedDecimal = totalDiscountAppliedDecimal.plus(
        lineItemDiscountInCents
      );

      const lineItemSubtotalAfterDiscountInCents =
        itemOriginalSubtotalDecimal.minus(lineItemDiscountInCents);
      subtotalAfterDiscountDecimal = subtotalAfterDiscountDecimal.plus(
        lineItemSubtotalAfterDiscountInCents
      );

      const taxRateDecimal = new Decimal(lineItem.cartItem.taxRate ?? 0); // taxRate is like 0.08 for 8%
      const taxOnLineItemInCents =
        lineItemSubtotalAfterDiscountInCents.mul(taxRateDecimal);
      totalTaxDecimal = totalTaxDecimal.plus(taxOnLineItemInCents);
    });

    let grandTotalDecimal = subtotalAfterDiscountDecimal.plus(totalTaxDecimal); // in cents
    let walletUsedDecimal = new Decimal(0); // in cents

    if (uWalletBalance) {
      // uWalletBalance is the boolean from useWalletBalance store
      if (walletBalanceDecimal.lessThan(grandTotalDecimal)) {
        walletUsedDecimal = walletBalanceDecimal;
        grandTotalDecimal = grandTotalDecimal.minus(walletBalanceDecimal);
        walletBalanceDecimal = new Decimal(0);
      } else {
        walletUsedDecimal = grandTotalDecimal;
        walletBalanceDecimal = walletBalanceDecimal.minus(grandTotalDecimal);
        grandTotalDecimal = new Decimal(0);
      }
    }

    // All values are divided by 100 for currency formatting as they are in cents
    return {
      subtotalPreDiscount: currencyFormatter.format(
        subtotalPreDiscountDecimal.div(100).toNumber()
      ),
      totalDiscount: currencyFormatter.format(
        totalDiscountAppliedDecimal.div(100).toNumber()
      ),
      subtotal: currencyFormatter.format(
        subtotalAfterDiscountDecimal.div(100).toNumber()
      ), // Subtotal after discount
      totalTax: currencyFormatter.format(totalTaxDecimal.div(100).toNumber()),
      grandTotal: currencyFormatter.format(
        grandTotalDecimal.div(100).toNumber()
      ),
      grandTotalNumber: grandTotalDecimal.div(100).toNumber(), // For potential use elsewhere
      walletUsed: currencyFormatter.format(
        walletUsedDecimal.div(100).toNumber()
      ),
      walletUsedNumber: walletUsedDecimal.round().toNumber(), // For backend (in cents)
      walletBalance: currencyFormatter.format(
        walletBalanceDecimal.div(100).toNumber()
      ),
      // Raw numbers in cents for purchase function or other logic if needed
      subtotalAfterDiscountCents: subtotalAfterDiscountDecimal
        .round()
        .toNumber(),
      totalDiscountAppliedCents: totalDiscountAppliedDecimal.round().toNumber(),
      totalTaxCents: totalTaxDecimal.round().toNumber(),
      grandTotalCents: grandTotalDecimal.round().toNumber(),
    };
  }
); // End of ledger

export function countCartItem(cartItem) {
  const items = cart.get().filter((item) => item.cartItem.id === cartItem.id);
  return items.length;
}
